<template>
  <div>
    <div class="mb-8 flex items-start justify-between">
      <div>
        <h1 class="text-3xl font-bold">
          <Link
            class="text-indigo-500 hover:text-indigo-800"
            :href="blocksRoute"
          >
            {{ blocksLabel }}
          </Link>
          <span class="font-medium text-indigo-400">/</span>
          {{ form.name }}
        </h1>
        <div class="mt-2">
          <span class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {{ block.template.name }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <Link
          class="text-sm font-medium text-gray-700 hover:text-gray-900"
          :href="blocksRoute"
        >
          Anuluj
        </Link>
        <loading-button
          :loading="form.processing"
          class="btn-indigo"
          @click="submit"
        >
          Zapisz blok
        </loading-button>
      </div>
    </div>

    <div class="space-y-6">
      <div class="overflow-hidden rounded-lg border-2 border-indigo-200 bg-indigo-50 shadow-sm">
        <form @submit.prevent="submit">
          <div class="px-6 py-5">
            <text-input
              v-model="form.name"
              :errors="form.errors.name"
              label="Nazwa bloku"
            />
          </div>
        </form>
      </div>

      <div class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="px-6 py-5">
          <text-input
            v-model="anchorId"
            label="Identyfikator sekcji (kotwica)"
            placeholder="np. nasze-podejscie"
          />
          <p class="mt-1 text-xs text-gray-500">
            Wspólny dla obu języków. Pasek „Context Navigation Tabs” linkuje do tej
            sekcji przez <code>#identyfikator</code> — wartość musi pasować do pola
            „ID (kotwica)” wybranej zakładki. Zostaw puste, aby nie dodawać kotwicy.
          </p>
        </div>
      </div>

      <div v-if="block.template.schema.fields.length > 0" class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="flex align-items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 px-6 py-3">Treść bloku</h2>
          <div class="flex items-center justify-between p-2 gap-2">
            <button
              type="button"
              :class="[
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                editingLocale === 'pl'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              ]"
              @click.stop.prevent="switchEditingLocale('pl')"
            >
              PL
            </button>
            <button
              type="button"
              :class="[
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                editingLocale === 'en'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              ]"
              @click.stop.prevent="switchEditingLocale('en')"
            >
              EN
            </button>
          </div>
        </div>

        <form class="px-6 py-5" @submit.prevent="submit">
          <block-schema-fields
            :fields="block.template.schema.fields"
            :form="form"
            :editing-locale="editingLocale"
            :linkable-pages="linkablePages"
            :existing-images="block.images || {}"
            :get-field-value="getFieldValue"
            :set-field-value="setFieldValue"
          />
        </form>
      </div>
      <div v-else>
        <p class="p-2 text-red-500">Ten szablon nie ma pól.</p>
      </div>

      <div class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div class="flex items-center gap-2">
            <icon name="eye" class="h-5 w-5 text-indigo-500 stroke-current" />
            <h2 class="text-lg font-semibold text-gray-900">Podgląd na żywo</h2>
          </div>
          <p class="mt-1 text-xs text-gray-500">
            Zobacz, jak blok wygląda podczas edycji
          </p>
        </div>
        <div class="bg-gray-50 p-2">
          <block-preview compact :block="blockForPreview" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  layout: createLayoutHelper,
};
</script>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { Link, useForm } from '@inertiajs/vue3';
import BlockPreview from '@/Shared/BlockPreview.vue';
import BlockSchemaFields from '@/Shared/BlockSchemaFields.vue';
import Icon from '@/Shared/Icon.vue';
import LoadingButton from '@/Shared/LoadingButton.vue';
import TextInput from '@/Shared/TextInput.vue';
import { getBlocksIndexRoute, getUpdateBlockRoute } from '@/utils/createBlockRouteHelper';
import { blockKindLabelPl } from '@/utils/blockKindLabels';

const { proxy } = getCurrentInstance();
const routes = proxy.$routes;

const props = defineProps({
  block: {
    type: Object,
    required: true,
  },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: {
    type: String,
    default: null,
  },
  linkablePages: {
    type: Array,
    default: () => [],
  },
});

const pagesById = computed(() =>
  Object.fromEntries(props.linkablePages.map((page) => [page.id, page])),
);

const formData = {
  name: props.block.name,
  template_id: props.block.template.id,
  images: {},
  data_pl: props.block.data_pl || {},
  data_en: props.block.data_en || {},
};

if (props.block.template?.schema?.fields) {
  props.block.template.schema.fields.forEach((field) => {
    if (field.type === 'image') {
      formData.images[field.name] = null;
    } else {
      if (!(field.name in formData.data_pl)) formData.data_pl[field.name] = null;
      if (!(field.name in formData.data_en)) formData.data_en[field.name] = null;
    }
  });
}

const form = useForm(formData);
const editingLocale = ref('pl');

// Anchor id is a structural, non-localized setting: a single input mirrored into
// both locales' data under the reserved `anchorId` key. DynamicBlock reads it to
// render the in-page `<section :id>` that Context Navigation Tabs link to.
const anchorId = computed({
  get() {
    return form.data_pl?.anchorId ?? form.data_en?.anchorId ?? '';
  },
  set(value) {
    const next = value ?? '';
    if (!form.data_pl) form.data_pl = {};
    if (!form.data_en) form.data_en = {};
    form.data_pl.anchorId = next;
    form.data_en.anchorId = next;
  },
});

const blocksLabel = computed(() => blockKindLabelPl(props?.block?.kind));

const blocksRoute = computed(() => getBlocksIndexRoute(routes, props.block.site_context, props.block.kind));

const blockForPreview = computed(() => {
  const data = {};
  const currentData = form[`data_${editingLocale.value}`] || {};
  if (props.block.template?.schema?.fields) {
    props.block.template.schema.fields.forEach((field) => {
      if (field.type !== 'image') {
        const value = currentData[field.name];
        if (value !== undefined && value !== null) {
          data[field.name] = value;
        }
      }
    });
  }

  const imagesByField = {};
  if (props.block.template?.schema?.fields) {
    const imageFields = props.block.template.schema.fields.filter((field) => field.type === 'image');
    imageFields.forEach((field) => {
      const newFile = form.images[field.name];
      if (newFile && newFile instanceof File) {
        imagesByField[field.name] = URL.createObjectURL(newFile);
      } else if (props.block.images && props.block.images[field.name]) {
        const imageValue = props.block.images[field.name];
        imagesByField[field.name] =
          typeof imageValue === 'string' ? imageValue : imageValue.url;
      }
    });
  }

  const previewData = resolvePageLinksInData({
    ...(props.block.data || {}),
    ...data,
  });
  const normalizedAnchorId = String(anchorId.value || '').trim();
  if (normalizedAnchorId) {
    previewData.anchorId = normalizedAnchorId;
  } else {
    delete previewData.anchorId;
  }

  return {
    id: props.block.id,
    name: form.name,
    kind: props.block.kind,
    anchor_id: normalizedAnchorId || null,
    data: previewData,
    images: imagesByField,
    template: props.block.template || null,
    site_context: props.block.site_context,
  };
});

const resolvePageLinksInData = (data) => {
  const resolved = { ...data };
  const aktualnosciPageId = resolved.aktualnosciPageId;
  const contactPageId = resolved.contactPageId;

  if (aktualnosciPageId != null && pagesById.value[aktualnosciPageId]) {
    resolved.aktualnosciUrl = pagesById.value[aktualnosciPageId].path;
  }
  if (contactPageId != null && pagesById.value[contactPageId]) {
    resolved.contactUrl = pagesById.value[contactPageId].path;
  }
  const buttonPageId = resolved.buttonPageId;
  if (buttonPageId != null && pagesById.value[buttonPageId]) {
    resolved.buttonUrl = pagesById.value[buttonPageId].path;
  }
  return resolved;
};

const getFieldValue = (fieldName) => {
  const localeData = form[`data_${editingLocale.value}`] || {};
  return localeData[fieldName] ?? null;
};

const setFieldValue = (fieldName, value) => {
  if (!form[`data_${editingLocale.value}`]) {
    form[`data_${editingLocale.value}`] = {};
  }
  form[`data_${editingLocale.value}`][fieldName] = value;
};

const switchEditingLocale = (locale) => {
  editingLocale.value = locale;
};

const submit = () => {
  const images = {};
  Object.keys(form.images).forEach((fieldName) => {
    const file = form.images[fieldName];
    if (file && file instanceof File) {
      images[fieldName] = file;
    }
  });

  form
    .transform(() => ({
      name: form.name,
      template_id: form.template_id,
      data_pl: form.data_pl || {},
      data_en: form.data_en || {},
      images,
    }))
    .put(getUpdateBlockRoute(routes, props.block.site_context, props.block.id, props.block.kind));
};
</script>
